import { Paper, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { alpha } from "@mui/material/styles";
import FlexBox from "../../../components/layouts/Flexbox";

export type InvitationDeclineStatusVariant =
  | "invalid-token"
  | "error"
  | "success";

const STATUS_COPY: Record<
  InvitationDeclineStatusVariant,
  { title: string; description: string }
> = {
  "invalid-token": {
    title: "Invalid Invitation Link",
    description:
      "The invitation link is missing a valid token. Please contact support if you continue to experience issues.",
  },
  error: {
    title: "Unable to Process Request",
    description:
      "The invitation link may be invalid or expired. Please contact support if you continue to experience issues.",
  },
  success: {
    title: "Invitation Declined Successfully",
    description: "You have successfully declined the project invitation.",
  },
};

type Props = {
  variant: InvitationDeclineStatusVariant;
};

export default function InvitationDeclineStatus({ variant }: Props) {
  const { title, description } = STATUS_COPY[variant];
  const isSuccess = variant === "success";

  return (
    <Stack
      minHeight="90vh"
      alignItems="center"
      justifyContent="center"
      p={2}
    >
      <Paper
        elevation={0}
        sx={(theme) => ({
          maxWidth: 500,
          width: 1,
          p: 4,
          textAlign: "center",
          borderRadius: 2,
          border: 1,
          borderStyle: "solid",
          borderColor: isSuccess ? "success.light" : "error.light",
          bgcolor: isSuccess
            ? alpha(theme.palette.success.main, 0.08)
            : alpha(theme.palette.error.main, 0.08),
        })}
      >
        <Stack alignItems="center" gap={3}>
          <FlexBox justifyContent="center">
            {isSuccess ? (
              <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
            ) : (
              <ErrorOutlineIcon color="error" sx={{ fontSize: 64 }} />
            )}
          </FlexBox>
          <Stack gap={2} alignItems="center">
            <Typography
              variant="h5"
              fontWeight={600}
              color={isSuccess ? "success" : "error"}
            >
              {title}
            </Typography>
            <Typography variant="body1" color="text.secondary" lineHeight={1.6}>
              {description}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
